import React from 'react';
import { Droplets, Wind, Thermometer, Loader } from 'lucide-react';
import {
  getWeatherDescription,
  getWeatherIcon,
  getAqiLabel,
} from '../../services/apiService/weatherApi';

/**
 * Weather panel showing current conditions, 6-hour forecast, and air quality.
 * Designed to fit the 380px-wide right sidebar on a 1024x600 kiosk.
 */
const WeatherPanel = ({ weatherData, weatherLoading }) => {
  // Loading state
  if (weatherLoading && !weatherData) {
    return (
      <div className="flex items-center justify-center py-6 text-white/60">
        <Loader size={18} className="animate-spin mr-2" />
        <span className="text-sm">Loading weather...</span>
      </div>
    );
  }

  // No data
  if (!weatherData) {
    return (
      <div className="text-center py-4 text-white/50 text-sm">
        Weather unavailable
      </div>
    );
  }

  const { current, hourly, daily, airQuality, units } = weatherData;

  // Format hour label from ISO string  e.g. "2025-06-14T15:00" -> "3 PM"
  const formatHour = (isoStr) => {
    try {
      const d = new Date(isoStr);
      return d.toLocaleTimeString([], { hour: 'numeric', hour12: true });
    } catch {
      return '--';
    }
  };

  const aqiInfo = airQuality ? getAqiLabel(airQuality.aqi) : null;

  return (
    <div className="space-y-3">
      {/* ---- Current Conditions ---- */}
      <div className="flex items-center gap-3">
        {/* Big weather icon */}
        <div className="flex-shrink-0">
          {getWeatherIcon(current.weatherCode, 36, !current.isDay)}
        </div>

        {/* Temp + description */}
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2">
            <span className="text-2xl font-bold text-white">
              {Math.round(current.temperature)}{units.temperature}
            </span>
            <span className="text-xs text-white/50">
              Feels {Math.round(current.feelsLike)}{units.temperature}
            </span>
          </div>
          <p className="text-sm text-white/70 truncate">
            {getWeatherDescription(current.weatherCode)}
          </p>
        </div>

        {/* Daily hi/lo */}
        <div className="flex-shrink-0 text-right text-xs text-white/60 leading-tight">
          <div className="flex items-center justify-end gap-1">
            <Thermometer size={12} className="text-red-400" />
            <span>{Math.round(daily.tempMax)}{units.temperature}</span>
          </div>
          <div className="flex items-center justify-end gap-1">
            <Thermometer size={12} className="text-blue-400" />
            <span>{Math.round(daily.tempMin)}{units.temperature}</span>
          </div>
        </div>
      </div>

      {/* Humidity + Wind row */}
      <div className="flex gap-4 text-xs text-white/70">
        <span className="flex items-center gap-1">
          <Droplets size={12} className="text-blue-300" />
          {current.humidity}%
        </span>
        <span className="flex items-center gap-1">
          <Wind size={12} className="text-cyan-300" />
          {Math.round(current.windSpeed)} {units.windSpeed}
        </span>
      </div>

      {/* ---- 6-Hour Forecast ---- */}
      {hourly.length > 0 && (
        <div>
          <h4 className="text-xs text-white/50 mb-1 uppercase tracking-wider">Forecast</h4>
          <div className="flex justify-between">
            {hourly.map((h, i) => (
              <div key={i} className="flex flex-col items-center gap-0.5 min-w-0">
                <span className="text-[10px] text-white/50">{formatHour(h.time)}</span>
                {getWeatherIcon(h.weatherCode, 16, !h.isDay)}
                <span className="text-xs text-white font-medium">
                  {Math.round(h.temperature)}&deg;
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ---- Air Quality ---- */}
      {aqiInfo && airQuality && (
        <div>
          <h4 className="text-xs text-white/50 mb-1 uppercase tracking-wider">Air Quality</h4>
          <div className="flex items-center gap-3 text-sm">
            <span className={`font-semibold ${aqiInfo.colorClass}`}>
              {airQuality.aqi} &mdash; {aqiInfo.label}
            </span>
            <span className="text-white/50 text-xs">
              PM2.5: {airQuality.pm25 != null ? airQuality.pm25.toFixed(1) : '--'}
            </span>
            <span className="text-white/50 text-xs">
              PM10: {airQuality.pm10 != null ? airQuality.pm10.toFixed(1) : '--'}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default WeatherPanel;
